import { useEffect, useRef, useState } from "react";

interface ShareButtonsProps {
  title: string;
}

export default function ShareButtons({ title }: ShareButtonsProps) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const channels = [
    { key: "wechat", label: "微信", icon: "ri-wechat-line", hint: "打开微信扫码分享" },
    { key: "weibo", label: "微博", icon: "ri-weibo-line", hint: "分享到微博（演示）" },
    { key: "qq", label: "QQ", icon: "ri-qq-line", hint: "分享给 QQ 好友（演示）" },
  ];

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-2 rounded-full border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 cursor-pointer whitespace-nowrap"
      >
        <i className="ri-share-forward-line w-4 h-4 flex items-center justify-center"></i>
        分享
      </button>

      {open && (
        <div className="absolute right-0 top-12 z-30 w-64 rounded-lg border border-background-200 bg-background-50 p-4 shadow-none">
          <p className="text-sm font-medium text-foreground-900">分享这篇文章</p>
          <p className="mt-1 line-clamp-1 text-xs text-foreground-500">{title}</p>
          <div className="mt-3 flex items-center justify-between">
            {channels.map((channel) => (
              <button
                key={channel.key}
                type="button"
                title={channel.hint}
                onClick={() => setCopied(false)}
                className="flex flex-col items-center gap-1.5 cursor-pointer"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-background-100 text-foreground-700 transition-colors hover:bg-primary-500 hover:text-background-50">
                  <i className={`${channel.icon} text-xl`}></i>
                </span>
                <span className="text-xs text-foreground-600">{channel.label}</span>
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={handleCopy}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className={copied ? "ri-check-line" : "ri-link"}></i>
            {copied ? "链接已复制" : "复制链接"}
          </button>
        </div>
      )}
    </div>
  );
}